/**
 * Wishlist functionality using localStorage
 * Handles adding/removing products from wishlist and managing wishlist state
 */

class WishlistManager {
  constructor() {
    this.storageKey = "shopify_wishlist";
    this.wishlist = this.loadWishlist();
    this.init();
  }

  /**
   * Initialize wishlist functionality
   */
  init() {
    this.bindEvents();
    this.updateWishlistButtons();
    this.updateWishlistCount();
  }

  /**
   * Load wishlist from localStorage
   */
  loadWishlist() {
    try {
      const stored = localStorage.getItem(this.storageKey);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error("Error loading wishlist:", error);
      return [];
    }
  }

  /**
   * Save wishlist to localStorage
   */
  saveWishlist() {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(this.wishlist));
      this.updateWishlistCount();
    } catch (error) {
      console.error("Error saving wishlist:", error);
    }
  }

  /**
   * Add product to wishlist
   */
  addToWishlist(productData) {
    const existingIndex = this.wishlist.findIndex(
      (item) => item.id === productData.id
    );

    if (existingIndex === -1) {
      this.wishlist.push(productData);
      this.saveWishlist();
      this.showNotification("Producto agregado a favoritos", "success");
      return true;
    }
    return false;
  }

  /**
   * Remove product from wishlist
   */
  removeFromWishlist(productId) {
    const index = this.wishlist.findIndex((item) => item.id === productId);
    if (index !== -1) {
      this.wishlist.splice(index, 1);
      this.saveWishlist();
      this.showNotification("Producto eliminado de favoritos", "info");
      return true;
    }
    return false;
  }

  /**
   * Check if product is in wishlist
   */
  isInWishlist(productId) {
    return this.wishlist.some((item) => item.id === productId);
  }

  /**
   * Get wishlist count
   */
  getWishlistCount() {
    return this.wishlist.length;
  }

  /**
   * Get all wishlist items
   */
  getWishlistItems() {
    return this.wishlist;
  }

  /**
   * Clear entire wishlist
   */
  clearWishlist() {
    this.wishlist = [];
    this.saveWishlist();
    this.showNotification("Lista de favoritos vaciada", "info");
  }

  /**
   * Bind event listeners
   */
  bindEvents() {
    // Wishlist button clicks
    document.addEventListener("click", (e) => {
      if (e.target.closest("[data-wishlist-toggle]")) {
        e.preventDefault();
        const button = e.target.closest("[data-wishlist-toggle]");
        const productId = button.dataset.productId;
        const productData = this.getProductData(button);

        if (this.isInWishlist(productId)) {
          this.removeFromWishlist(productId);
        } else {
          this.addToWishlist(productData);
        }

        this.updateWishlistButtons();
      }
    });

    // Mobile bottom nav wishlist dropdown toggle
    const mobileWishlistDropdown = document.querySelector(
      ".mobile-bottom-nav .wishlist-dropdown"
    );
    if (mobileWishlistDropdown) {
      mobileWishlistDropdown.addEventListener("click", (e) => {
        e.stopPropagation();
        mobileWishlistDropdown.classList.toggle("active");
      });

      document.addEventListener("click", (e) => {
        if (
          mobileWishlistDropdown.classList.contains("active") &&
          !mobileWishlistDropdown.contains(e.target)
        ) {
          mobileWishlistDropdown.classList.remove("active");
        }
      });
    }

    // Listen for product variant changes
    document.addEventListener("variant:change", (e) => {
      this.updateWishlistButtons();
    });
  }

  /**
   * Get product data from button context
   */
  getProductData(button) {
    const productForm =
      button.closest("product-form") || button.closest(".product-form");
    const productData = {
      id: button.dataset.productId,
      title:
        button.dataset.productTitle ||
        document
          .querySelector("h1.product-details-product-title")
          ?.textContent?.trim(),
      handle:
        button.dataset.productHandle ||
        window.location.pathname.split("/").pop(),
      price:
        button.dataset.productPrice ||
        document
          .querySelector(".product-price-group .money")
          ?.textContent?.trim(),
      image:
        button.dataset.productImage ||
        document.querySelector(".product-media img")?.src,
      url: button.dataset.productUrl || window.location.href,
      vendor: button.dataset.productVendor || "",
      type: button.dataset.productType || "",
      addedAt: new Date().toISOString(),
    };

    return productData;
  }

  /**
   * Update wishlist button states
   */
  updateWishlistButtons() {
    const buttons = document.querySelectorAll("[data-wishlist-toggle]");

    buttons.forEach((button) => {
      const productId = button.dataset.productId;
      const isInWishlist = this.isInWishlist(productId);

      // Update button state
      button.classList.toggle("wishlist-active", isInWishlist);
      button.setAttribute("aria-pressed", isInWishlist);

      // Update button text/icon
      const textElement = button.querySelector(".wishlist-text");
      const outlineElement = button.querySelector(".wishlist-heart-outline");
      const filledElement = button.querySelector(".wishlist-heart-filled");

      if (textElement) {
        textElement.textContent = isInWishlist
          ? "En favoritos"
          : "Agregar a favoritos";
      }

      // Update SVG heart states
      if (outlineElement && filledElement) {
        if (isInWishlist) {
          outlineElement.style.display = "none";
          filledElement.style.display = "block";
        } else {
          outlineElement.style.display = "block";
          filledElement.style.display = "none";
        }
      }
    });
  }

  /**
   * Update wishlist count display
   */
  updateWishlistCount() {
    const countElements = document.querySelectorAll("[data-wishlist-count]");
    const count = this.getWishlistCount();

    countElements.forEach((element) => {
      element.textContent = count;
      element.style.display = count > 0 ? "inline" : "none";
    });

    // Update header preview
    this.updateHeaderPreview();
  }

  /**
   * Update header wishlist preview dropdown
   */
  updateHeaderPreview() {
    const previewItems = document.getElementById("wishlist-preview-items");
    const previewEmpty = document.getElementById("wishlist-preview-empty");
    const mobilePreviewItems = document.getElementById(
      "custom-mobile-wishlist-preview-items"
    );
    const mobilePreviewEmpty = document.getElementById(
      "custom-mobile-wishlist-preview-empty"
    );

    const previews = [
      { items: previewItems, empty: previewEmpty },
      { items: mobilePreviewItems, empty: mobilePreviewEmpty },
    ];

    if (this.wishlist.length === 0) {
      previews.forEach((p) => {
        if (p.items) p.items.innerHTML = "";
        if (p.empty) p.empty.style.display = "block";
      });
      return;
    }

    const previewItemsList = this.wishlist.slice(0, 5);

    const itemsHtml = previewItemsList
      .map(
        (item) => `
        <div class="wishlist-preview__item" data-product-id="${item.id}">
          <div class="wishlist-preview__item-image">
            <a href="${item.url}">
              <img src="${item.image}" alt="${item.title}" loading="lazy">
            </a>
          </div>
          <div class="wishlist-preview__item-content" data-product-url="${item.url}" style="cursor: pointer;">
            <h4 class="wishlist-preview__item-title">${item.title}</h4>
            <p class="wishlist-preview__item-price">${item.price}</p>
          </div>
          <button class="wishlist-preview__item-remove" data-wishlist-toggle data-product-id="${item.id}" aria-label="Quitar de favoritos">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </div>
      `
      )
      .join("");

    previews.forEach((p) => {
      if (!p.items || !p.empty) return;

      p.items.innerHTML = itemsHtml;
      p.empty.style.display = "none";

      const previewItemContents = p.items.querySelectorAll(
        ".wishlist-preview__item-content"
      );
      previewItemContents.forEach((content) => {
        content.addEventListener("click", (e) => {
          e.stopPropagation();
          const productUrl = content.dataset.productUrl;
          if (productUrl) window.location.href = productUrl;
        });
      });
    });

    // 👇 NUEVO BLOQUE: también actualizar el contador del menú inferior
    const mobileWishlistCount = document.querySelector(
      ".mobile-bottom-nav [data-wishlist-count]"
    );
    if (mobileWishlistCount) {
      const count = this.getWishlistCount();
      mobileWishlistCount.textContent = count;
      mobileWishlistCount.style.display = count > 0 ? "inline" : "none";
    }
  }

  /**
   * Show notification
   */
  showNotification(message, type = "info") {
    // Create notification element
    const notification = document.createElement("div");
    notification.className = `wishlist-notification wishlist-notification--${type}`;
    notification.textContent = message;

    // Add to page
    document.body.appendChild(notification);

    // Show notification
    setTimeout(
      () => notification.classList.add("wishlist-notification--show"),
      100
    );

    // Remove notification after delay
    setTimeout(() => {
      notification.classList.remove("wishlist-notification--show");
      setTimeout(() => notification.remove(), 300);
    }, 3000);
  }

  /**
   * Render wishlist items (for wishlist page)
   */
  renderWishlistItems(container) {
    if (!container) return;

    if (this.wishlist.length === 0) {
      container.innerHTML = `
        <div class="wishlist-empty">
          <div class="wishlist-empty__icon">
            <i class="fa fa-heart"></i>
          </div>
          <h3 class="wishlist-empty__title">Tu lista de favoritos está vacía</h3>
          <p class="wishlist-empty__text">Agrega productos que te gusten para verlos aquí</p>
          <a href="/collections/all" class="btn btn--primary">Continuar comprando</a>
        </div>
      `;
      return;
    }

    const itemsHtml = this.wishlist
      .map(
        (item) => `
      <div class="wishlist-item" data-product-id="${item.id}">
        <div class="wishlist-item__image">
          <a href="${item.url}">
            <img src="${item.image}" alt="${item.title}" loading="lazy">
          </a>
        </div>
        <div class="wishlist-item__content">
          <h3 class="wishlist-item__title">
            <a href="${item.url}">${item.title}</a>
          </h3>
          <div class="wishlist-item__price">${item.price}</div>
          <div class="wishlist-item__actions">
            <button class="btn btn--primary btn--small" onclick="window.location.href='${item.url}'">
              Ver producto
            </button>
            <button class="btn btn--secondary btn--small" data-wishlist-toggle data-product-id="${item.id}">
              <svg class="wishlist-heart-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path class="wishlist-heart-filled" d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" fill="currentColor"/>
              </svg>
              Quitar
            </button>
          </div>
        </div>
      </div>
    `
      )
      .join("");

    container.innerHTML = `
      <div class="wishlist-items">
        ${itemsHtml}
      </div>
      <div class="wishlist-actions">
        <button class="btn btn--secondary" onclick="wishlistManager.clearWishlist()">
          Vaciar lista de favoritos
        </button>
      </div>
    `;

    // Add click handlers for wishlist items
    const wishlistItems = container.querySelectorAll(".wishlist-item");
    wishlistItems.forEach((item) => {
      const productId = item.dataset.productId;
      const productData = this.wishlist.find((p) => p.id === productId);

      if (productData) {
        item.addEventListener("click", (e) => {
          // Don't trigger if clicking on buttons or links
          if (e.target.closest("button") || e.target.closest("a")) {
            return;
          }
          window.location.href = productData.url;
        });
      }
    });
  }
}

// Initialize wishlist manager
const wishlistManager = new WishlistManager();

// Make it globally available
window.wishlistManager = wishlistManager;

// Export for module usage
if (typeof module !== "undefined" && module.exports) {
  module.exports = WishlistManager;
}

// --- Mobile wishlist: open/close preview ---
document.addEventListener("DOMContentLoaded", function () {
  const mobileWishlistButton = document.querySelector(
    ".custom-mobile-bottom-nav__link.custom-wishlist-dropdown"
  );
  const mobileWishlistPreview = document.getElementById(
    "custom-mobile-wishlist-preview"
  );

  if (mobileWishlistButton && mobileWishlistPreview) {
    mobileWishlistButton.addEventListener("click", (e) => {
      e.stopPropagation();
      mobileWishlistPreview.classList.toggle("active");
    });

    // Close if clicked outside the dropdown
    document.addEventListener("click", (e) => {
      if (
        !mobileWishlistButton.contains(e.target) &&
        !mobileWishlistPreview.contains(e.target)
      ) {
        mobileWishlistPreview.classList.remove("active");
      }
    });
  }
});
